import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useBranch } from '@/context/BranchContext';
import { loadDraft, saveDraft, clearDraft } from '@/lib/draftStorage';
import { useProducts } from '@/hooks/useProducts';
import { useActiveOffers } from '@/hooks/useActiveOffers';
import { useSalesSettings } from '@/hooks/useSalesSettings';
import { MIN_DISCOUNT_UNIT_PRICE, DEFAULT_PAYMENT_METHODS } from '@/lib/salesSettings';
import { usePosShortcuts, SHORTCUTS } from '@/hooks/usePosShortcuts';
import { formatMoney } from '@/lib/format';
import { friendlyError } from '@/lib/errors';
import ProductImage from '@/components/feature/ProductImage';
import { newIdempotencyKey } from '@/lib/fiscal/idempotency';
import { playAddSound, playSaleSound, isSoundMuted, setSoundMuted } from '@/lib/sound';
import type { CartItem, Client, Product, ProductDiscount, WholesaleTier, LinePricing, ManualDiscount } from '@/lib/types';
import CartPanel from '@/pages/pos/components/CartPanel';
import CheckoutModal from '@/pages/pos/components/CheckoutModal';
import BarcodeScanner from '@/pages/pos/components/BarcodeScanner';
import ClientPicker from '@/pages/pos/components/ClientPicker';
import DiscountModal from '@/pages/pos/components/DiscountModal';
import Modal from '@/components/base/Modal';

const round2 = (n: number) => Math.round(n * 100) / 100;

function isDiscountActive(d: ProductDiscount): boolean {
  if (!d.active) return false;
  if (d.starts_at && new Date(d.starts_at) > new Date()) return false;
  if (d.ends_at && new Date(d.ends_at) < new Date()) return false;
  return true;
}

export default function PosPage() {
  const navigate = useNavigate();
  const { user, isAdmin, profile } = useAuth();
  const { branches, activeBranch } = useBranch();
  const cartKey = user?.id ? `pos:cart:${user.id}` : 'pos:cart';
  const customerKey = user?.id ? `pos:customer:${user.id}` : 'pos:customer';
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState<CartItem[]>(() => loadDraft<CartItem[]>(cartKey) ?? []);
  const [customer, setCustomer] = useState<Client | null>(() => loadDraft<Client>(customerKey));
  const [clients, setClients] = useState<Client[]>([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [completed, setCompleted] = useState<{ id: string; invoice_number: string; e_ncf: string | null } | null>(null);
  const [processing, setProcessing] = useState(false);
  const [scanMsg, setScanMsg] = useState<string | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [clientModal, setClientModal] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');

  // Buscador activo en la barra unificada
  const [activeSearch, setActiveSearch] = useState<'client' | 'product' | 'discount' | null>('product');

  // Descuentos y mayoristas
  const [discountCode, setDiscountCode] = useState('');
  const [appliedCode, setAppliedCode] = useState<{ code: string; scope: string; percentage: number } | null>(null);
  const [codeMsg, setCodeMsg] = useState<string | null>(null);
  const [productDiscounts, setProductDiscounts] = useState<Record<string, ProductDiscount>>({});
  const [wholesaleTiers, setWholesaleTiers] = useState<Record<string, WholesaleTier[]>>({});
  const [wholesaleMode, setWholesaleMode] = useState(false);

  const isCashier = !isAdmin;
  // El cajero SIEMPRE usa su propia sucursal; nunca cae a branches[0] (otra sucursal).
  const sellBranch = isCashier ? activeBranch : (activeBranch ?? branches[0]);
  const sellBranchId = sellBranch?.id ?? null;

  const { products, loading, error, hasMore, loadMore, reload } = useProducts({
    search,
    activeOnly: true,
    branchId: sellBranchId,
  });

  // Ofertas activas (por producto) según fecha/hora actual.
  const offerIds = useMemo(
    () => [...new Set([...products.map((p) => p.id), ...cart.map((i) => i.product.id)])],
    [products, cart]
  );
  const { offersByProduct } = useActiveOffers(offerIds);

  // Atajos de teclado + selección de línea
  const searchRef = useRef<HTMLInputElement>(null);
  const discountRef = useRef<HTMLInputElement>(null);
  const checkoutIdempotencyKeyRef = useRef<string>('');
  // Productos cuyo descuento / precio mayorista ya se consultó en esta sesión.
  const loadedPricingIdsRef = useRef<Set<string>>(new Set());
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [qtyModal, setQtyModal] = useState(false);
  const [qtyValue, setQtyValue] = useState('');
  const [discountLineId, setDiscountLineId] = useState<string | null>(null);
  const [removeConfirm, setRemoveConfirm] = useState(false);
  const [newSaleConfirm, setNewSaleConfirm] = useState(false);
  const [soundOn, setSoundOn] = useState<boolean>(() => !isSoundMuted());

  const { settings } = useSalesSettings();

  const currencySymbol = settings?.currency_symbol ?? 'RD$';
  const taxesEnabled = settings?.taxes_enabled ?? false;
  const taxPercentage = settings?.tax_percentage ?? 0;
  const discountsEnabled = settings?.discounts_enabled ?? false;
  const discountPercentageAllowed = settings?.discount_percentage_allowed ?? true;
  const discountFixedAllowed = settings?.discount_fixed_allowed ?? true;
  const discountMaxPercentage = settings?.discount_max_percentage ?? 0;
  const discountAllowedRoles = settings?.discount_allowed_roles ?? 'admin';
  const allowSellWithoutStock = settings?.allow_sell_without_stock ?? false;
  const requireReceived = settings?.cash_require_received ?? true;
  const paymentMethods = settings?.payment_methods ?? DEFAULT_PAYMENT_METHODS;

  // Un descuento manual respeta los permisos configurados.
  const canApplyDiscount =
    discountsEnabled && (discountAllowedRoles === 'admin_cashier' || isAdmin);
  const currentUserName =
    [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') ||
    profile?.username ||
    profile?.email ||
    null;

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }, []);

  useEffect(() => {
    if (activeSearch === 'product') searchRef.current?.focus();
    else if (activeSearch === 'discount') discountRef.current?.focus();
  }, [activeSearch]);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    setSoundMuted(!next);
    if (next) playAddSound();
  };

  useEffect(() => {
    let active = true;
    const loadClients = async () => {
      const { data } = await supabase.from('clients').select('*').order('name');
      if (active) setClients((data as Client[]) ?? []);
    };
    loadClients();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    // Solo se consultan los productos nuevos (paginación/búsqueda), evitando
    // repetir la descarga completa de descuentos y precios mayoristas en cada
    // búsqueda o al cargar más productos.
    const pending = products
      .map((p) => p.id)
      .filter((id) => !loadedPricingIdsRef.current.has(id));
    if (pending.length === 0) return;

    pending.forEach((id) => loadedPricingIdsRef.current.add(id));
    const ids = pending;
    let active = true;

    const loadDiscounts = async () => {
      try {
        const { data: d } = await supabase
          .from('product_discounts')
          .select('*')
          .in('product_id', ids)
          .eq('active', true);
        const { data: t } = await supabase
          .from('wholesale_tiers')
          .select('*')
          .in('product_id', ids);
        if (!active) return;
        const dMap: Record<string, ProductDiscount> = {};
        ((d as ProductDiscount[]) ?? []).forEach((x) => {
          dMap[x.product_id] = x;
        });
        const tMap: Record<string, WholesaleTier[]> = {};
        ((t as WholesaleTier[]) ?? []).forEach((x) => {
          (tMap[x.product_id] = tMap[x.product_id] ?? []).push(x);
        });
        setProductDiscounts((prev) => ({ ...prev, ...dMap }));
        setWholesaleTiers((prev) => ({ ...prev, ...tMap }));
      } catch {
        // Si falla, se libera el bloqueo para poder reintentar en la próxima carga.
        ids.forEach((id) => loadedPricingIdsRef.current.delete(id));
      }
    };
    loadDiscounts();
    return () => {
      active = false;
    };
  }, [products]);

  useEffect(() => {
    if (cart.length === 0) clearDraft(cartKey);
    else saveDraft(cartKey, cart);
  }, [cart, cartKey]);

  useEffect(() => {
    if (customer) saveDraft(customerKey, customer);
    else clearDraft(customerKey);
  }, [customer, customerKey]);

  const addToCart = useCallback(
    (product: Product) => {
      const maxQty = allowSellWithoutStock ? Number.MAX_SAFE_INTEGER : product.stock;
      const existing = cart.find((i) => i.product.id === product.id);
      if (existing) {
        if (existing.quantity >= maxQty) return;
        setCart((prev) =>
          prev.map((i) =>
            i.product.id === product.id ? { ...i, quantity: i.quantity + 1 } : i
          )
        );
      } else {
        if (maxQty <= 0) return;
        setCart((prev) => [...prev, { product, quantity: 1, discount: 0 }]);
      }
      setSelectedLineId(product.id);
      playAddSound();
    },
    [allowSellWithoutStock, cart]
  );

  const increment = useCallback(
    (id: string) => {
      setCart((prev) =>
        prev.map((i) => {
          if (i.product.id !== id) return i;
          const maxQty = allowSellWithoutStock ? Number.MAX_SAFE_INTEGER : i.product.stock;
          if (i.quantity >= maxQty) return i;
          return { ...i, quantity: i.quantity + 1 };
        })
      );
    },
    [allowSellWithoutStock]
  );

  const decrement = useCallback((id: string) => {
    setCart((prev) =>
      prev
        .map((i) => (i.product.id === id ? { ...i, quantity: i.quantity - 1 } : i))
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const remove = useCallback((id: string) => {
    setCart((prev) => prev.filter((i) => i.product.id !== id));
    setSelectedLineId((prev) => (prev === id ? null : prev));
  }, []);

  const setManualDiscount = useCallback((id: string, discount: ManualDiscount | null) => {
    setCart((prev) =>
      prev.map((i) => (i.product.id === id ? { ...i, manualDiscount: discount } : i))
    );
  }, []);

  // Descuento rápido por porcentaje, aplicado en vivo desde la línea del carrito.
  const applyQuickPercent = useCallback(
    (id: string, percent: number | null) => {
      setManualDiscount(
        id,
        percent == null || percent <= 0
          ? null
          : {
              type: 'percent',
              value: percent,
              appliedByName: currentUserName,
              authorizedByName: null,
            }
      );
    },
    [setManualDiscount, currentUserName]
  );

  const selectedLine = cart.find((i) => i.product.id === selectedLineId) ?? null;
  const discountLine = cart.find((i) => i.product.id === discountLineId) ?? null;

  const applyCode = useCallback(async (raw: string) => {
    const code = raw.trim().toUpperCase();
    if (!code) return;
    const { data, error } = await supabase.rpc('validate_discount_code', { p_code: code });
    if (error) {
      setCodeMsg('Error al validar el código.');
      setAppliedCode(null);
      return;
    }
    const res = data as {
      valid: boolean;
      message?: string;
      code?: string;
      scope?: string;
      percentage?: number;
    };
    if (!res.valid) {
      setCodeMsg(res.message ?? 'Código inválido.');
      setAppliedCode(null);
      return;
    }
    setAppliedCode({ code: res.code!, scope: res.scope!, percentage: Number(res.percentage) });
    setCodeMsg(null);
    setDiscountCode('');
  }, []);

  const handleScan = useCallback(
    async (code: string) => {
      const term = code.trim();
      if (!term) return;
      const upper = term.toUpperCase();

      // Cliente mayorista por código
      if (upper.startsWith('MAY-')) {
        const { data } = await supabase
          .from('clients')
          .select('*')
          .eq('wholesale_code', upper)
          .maybeSingle();
        setSearch('');
        if (data) {
          const c = data as Client;
          if (!c.active) {
            setScanMsg('Este cliente mayorista está inactivo.');
            setTimeout(() => setScanMsg(null), 3000);
          } else {
            setCustomer(c);
            setScanMsg(null);
            showToast('Cliente mayorista encontrado');
          }
        } else {
          setScanMsg('Código mayorista no encontrado.');
          setTimeout(() => setScanMsg(null), 3000);
        }
        return;
      }

      // Código de descuento
      if (upper.startsWith('CAJ-') || upper.startsWith('ADMIN-')) {
        setSearch('');
        await applyCode(upper);
        return;
      }

      // Producto
      const { data } = await supabase
        .from('products')
        .select('*')
        .or(`barcode.eq.${term},sku.eq.${term}`)
        .eq('active', true)
        .limit(1);
      const raw = (data as Product[])?.[0];
      if (raw) {
        // Leer el stock real de la sucursal activa (misma fuente que la cuadrícula).
        let p = raw;
        if (sellBranchId) {
          const { data: inv } = await supabase
            .from('branch_inventory')
            .select('stock')
            .eq('branch_id', sellBranchId)
            .eq('product_id', raw.id)
            .maybeSingle();
          p = { ...raw, stock: Number((inv as { stock: number } | null)?.stock ?? 0) };
        }
        addToCart(p);
        setSearch('');
        setScanMsg(null);
      } else {
        setScanMsg(`No se encontró el producto con código "${term}".`);
        setTimeout(() => setScanMsg(null), 3000);
      }
    },
    [addToCart, applyCode, showToast, sellBranchId]
  );

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleScan(search);
    }
  };

  const isWholesaleCustomer = !!customer && customer.is_wholesale && customer.active;

  const pricing = useMemo(() => {
    const map: Record<string, LinePricing> = {};
    cart.forEach((item) => {
      const base = item.product.sale_price;
      let unitPrice = base;
      let isWholesale = false;
      let autoDiscountPct: number | null = null;
      let autoDiscountType: string | null = null;
      let autoDiscount = 0;
      let offerId: string | null = null;
      let offerName: string | null = null;
      if (isWholesaleCustomer && item.product.wholesale_enabled) {
        const tiers = (wholesaleTiers[item.product.id] ?? [])
          .filter((t) => t.min_quantity <= item.quantity)
          .sort((a, b) => b.min_quantity - a.min_quantity);
        if (tiers.length > 0) {
          unitPrice = tiers[0].price;
          isWholesale = true;
        }
      }
      if (
        !isWholesale &&
        wholesaleMode &&
        item.product.wholesale_price != null &&
        item.product.wholesale_price > 0
      ) {
        unitPrice = item.product.wholesale_price;
        isWholesale = true;
      }
      if (!isWholesale) {
        const offer = offersByProduct[item.product.id];
        if (offer) {
          autoDiscountPct = Number(offer.percentage);
          autoDiscountType = 'offer';
          autoDiscount = Math.round(base * item.quantity * autoDiscountPct) / 100;
          offerId = offer.id;
          offerName = offer.name;
        } else {
          const pd = productDiscounts[item.product.id];
          if (pd && isDiscountActive(pd)) {
            autoDiscountPct = Number(pd.percentage);
            autoDiscountType = 'product';
            autoDiscount = Math.round(base * item.quantity * autoDiscountPct) / 100;
          }
        }
      }

      // Descuento manual por artículo (independiente, se suma al automático).
      const lineBase = round2(unitPrice * item.quantity);
      const md = item.manualDiscount ?? null;
      let manualDiscount = 0;
      let manualDiscountPct: number | null = null;
      let manualDiscountType: 'percent' | 'fixed' | null = null;
      let manualDiscountValue = 0;
      if (md && md.value > 0) {
        manualDiscountType = md.type;
        manualDiscountValue = md.value;
        if (md.type === 'percent') {
          manualDiscountPct = round2(md.value);
          manualDiscount = round2((unitPrice * item.quantity * md.value) / 100);
        } else {
          manualDiscount = round2(md.value);
          manualDiscountPct = lineBase > 0 ? round2((md.value / lineBase) * 100) : 0;
        }
      }
      if (manualDiscount > lineBase) manualDiscount = lineBase;
      const totalDiscount = Math.min(round2(autoDiscount + manualDiscount), lineBase);

      map[item.product.id] = {
        unitPrice,
        discountAmount: totalDiscount,
        discountPct: autoDiscountPct,
        discountType: autoDiscountType,
        isWholesale,
        offerId,
        offerName,
        autoDiscount,
        autoDiscountPct,
        autoDiscountType,
        manualDiscount,
        manualDiscountPct,
        manualDiscountType,
        manualDiscountValue,
        manualAuthorizedByName: md?.authorizedByName ?? null,
      };
    });
    return map;
  }, [cart, isWholesaleCustomer, productDiscounts, wholesaleTiers, offersByProduct, wholesaleMode]);

  const totals = useMemo(() => {
    const subtotal = round2(
      cart.reduce(
        (s, i) => s + (pricing[i.product.id]?.unitPrice ?? i.product.sale_price) * i.quantity,
        0
      )
    );
    const lineDiscount = round2(
      cart.reduce((s, i) => s + (pricing[i.product.id]?.discountAmount ?? 0), 0)
    );
    const codeDiscount = appliedCode
      ? round2(((subtotal - lineDiscount) * appliedCode.percentage) / 100)
      : 0;
    const discount = round2(lineDiscount + codeDiscount);
    const taxable = subtotal - discount;
    const tax = taxesEnabled ? round2(taxable * (taxPercentage / 100)) : 0;
    const total = round2(taxable + tax);
    return { subtotal, discount, tax, total, lineDiscount, codeDiscount };
  }, [cart, pricing, appliedCode, taxesEnabled, taxPercentage]);

  const handleConfirm = async (method: string, amountReceived: number, changeAmount: number) => {
    setProcessing(true);
    try {
      if (!checkoutIdempotencyKeyRef.current) {
        checkoutIdempotencyKeyRef.current = newIdempotencyKey();
      }
      const idempotencyKey = checkoutIdempotencyKeyRef.current;
      const items = cart.map((i) => {
        const p = pricing[i.product.id] ?? {
          unitPrice: i.product.sale_price,
          discountAmount: 0,
          discountPct: null,
          discountType: null,
          isWholesale: false,
          offerId: null,
          offerName: null,
          autoDiscount: 0,
          autoDiscountPct: null,
          autoDiscountType: null,
          manualDiscount: 0,
          manualDiscountPct: null,
          manualDiscountType: null,
          manualDiscountValue: 0,
          manualAuthorizedByName: null,
        };
        const lineBase = p.unitPrice * i.quantity;
        const effectivePct =
          lineBase > 0 && p.discountAmount > 0
            ? Math.round((p.discountAmount / lineBase) * 10000) / 100
            : null;
        let lineDiscountType: string | null = p.autoDiscountType;
        if (p.manualDiscountType === 'percent') lineDiscountType = 'manual_percent';
        else if (p.manualDiscountType === 'fixed') lineDiscountType = 'manual_fixed';
        return {
          product_id: i.product.id,
          product_name: i.product.name,
          quantity: i.quantity,
          unit_price: p.unitPrice,
          discount: p.discountAmount,
          original_price: i.product.sale_price,
          discount_percentage: effectivePct,
          discount_type: lineDiscountType,
          is_wholesale: p.isWholesale,
          offer_id: p.offerId ?? null,
          offer_name: p.offerName ?? null,
          tax_percentage: i.product.tax_percentage ?? (taxesEnabled ? taxPercentage : 0),
          discount_applied_by_name: p.manualDiscountType ? currentUserName : null,
          discount_authorized_by_name: p.manualAuthorizedByName ?? null,
        };
      });
      const payload = {
        items,
        subtotal: totals.subtotal,
        discount: totals.discount,
        tax: totals.tax,
        total: totals.total,
        discount_code: appliedCode?.code ?? null,
        payment_method: method,
        amount_received: amountReceived,
        change_amount: changeAmount,
        customer_id: customer?.id ?? null,
        customer_name: customer?.name ?? null,
        status: settings?.sale_initial_status ?? 'completed',
        branch_id: sellBranchId,
        idempotency_key: idempotencyKey,
        document_type: '32',
        dgii_environment: 'CERTIFICATION',
      };
      const { data, error } = await supabase.rpc('complete_sale_with_ecf', { payload });
      if (error) throw error;

      checkoutIdempotencyKeyRef.current = '';
      setCompleted({ id: data.sale_id, invoice_number: data.invoice_number, e_ncf: data.e_ncf ?? null });
      setCart([]);
      setCustomer(null);
      setSelectedLineId(null);
      setShowCheckout(false);
      setCartOpen(false);
      setShowSuccess(true);
      playSaleSound();
      reload();
    } finally {
      setProcessing(false);
    }
  };

  const resetSale = () => {
    checkoutIdempotencyKeyRef.current = '';
    // Al iniciar una venta nueva se refrescan los descuentos vigentes.
    loadedPricingIdsRef.current.clear();
    setCart([]);
    setCustomer(null);
    setSelectedLineId(null);
    setShowSuccess(false);
    setCompleted(null);
    setAppliedCode(null);
    setCodeMsg(null);
    setDiscountCode('');
    setWholesaleMode(false);
    reload();
  };

  const handleAddClient = async () => {
    if (!newClientName.trim()) return;
    const { data, error } = await supabase
      .from('clients')
      .insert({ name: newClientName.trim(), phone: newClientPhone.trim() || null })
      .select('*')
      .single();
    if (error) {
      showToast(friendlyError(error, 'No se pudo guardar el cliente.'));
      return;
    }
    const c = data as Client;
    setClients((prev) => [...prev, c].sort((a, b) => a.name.localeCompare(b.name)));
    setCustomer(c);
    setNewClientName('');
    setNewClientPhone('');
    setClientModal(false);
  };

  // Atajos de teclado
  usePosShortcuts({
    onSearchFocus: () => {
      setActiveSearch('product');
      setTimeout(() => searchRef.current?.focus(), 0);
    },
    onFindClient: () => setClientModal(true),
    onNewProduct: () => {
      if (isAdmin) navigate('/products/new');
      else showToast('No tienes permiso para crear productos.');
    },
    onChangeQuantity: () => {
      if (!selectedLine) {
        showToast('Selecciona un producto del carrito primero.');
        return;
      }
      setQtyValue(String(selectedLine.quantity));
      setQtyModal(true);
    },
    onReload: () => {
      reload();
      showToast('Productos actualizados.');
    },
    onDiscount: () => {
      if (!canApplyDiscount) {
        showToast('No tienes permiso para aplicar descuentos.');
        return;
      }
      if (!selectedLine) {
        showToast('Selecciona un producto del carrito primero.');
        return;
      }
      const unitPrice =
        pricing[selectedLine.product.id]?.unitPrice ?? selectedLine.product.sale_price;
      const hasManual =
        !!selectedLine.manualDiscount && selectedLine.manualDiscount.value > 0;
      if (unitPrice < MIN_DISCOUNT_UNIT_PRICE && !hasManual) {
        showToast(
          `El descuento extra solo aplica a productos de ${formatMoney(
            MIN_DISCOUNT_UNIT_PRICE,
            currencySymbol
          )} o más.`
        );
        return;
      }
      setDiscountLineId(selectedLine.product.id);
    },
    onPaymentMethod: () => {
      if (cart.length === 0) showToast('No hay productos en la venta.');
      else setShowCheckout(true);
    },
    onRemoveProduct: () => {
      if (!selectedLine) {
        showToast('Selecciona un producto del carrito primero.');
        return;
      }
      setRemoveConfirm(true);
    },
    onSuspendSale: () => showToast('Función en preparación.'),
    onNewSale: () => {
      if (cart.length === 0) resetSale();
      else setNewSaleConfirm(true);
    },
    onViewSales: () => navigate('/sales'),
    onCheckout: () => {
      if (cart.length === 0) showToast('No hay productos en la venta.');
      else setShowCheckout(true);
    },
  });

  const applyQty = () => {
    const q = parseInt(qtyValue, 10);
    if (!selectedLine || Number.isNaN(q) || q <= 0) {
      setQtyModal(false);
      return;
    }
    const maxQty = allowSellWithoutStock ? Number.MAX_SAFE_INTEGER : selectedLine.product.stock;
    setCart((prev) =>
      prev.map((i) =>
        i.product.id === selectedLine.product.id ? { ...i, quantity: Math.min(q, maxQty) } : i
      )
    );
    setQtyModal(false);
  };

  if (showSuccess && completed) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <div className="w-20 h-20 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center">
          <i className="ri-checkbox-circle-line text-4xl" />
        </div>
        <h1 className="mt-6 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
          Venta completada correctamente.
        </h1>
        <p className="mt-2 text-foreground-600">
          Factura <span className="font-semibold text-foreground-950">{completed.invoice_number}</span>
        </p>
        {completed.e_ncf && (
          <p className="mt-1 text-sm text-foreground-600">
            Comprobante fiscal{' '}
            <span className="font-mono font-semibold text-foreground-950">{completed.e_ncf}</span>
          </p>
        )}
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => navigate(`/sales/${completed.id}`, { state: { print: true } })}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 px-5 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
          >
            <i className="ri-printer-line" /> Imprimir factura
          </button>
          <button
            onClick={resetSale}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-3 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
          >
            <i className="ri-add-line" /> Nueva venta
          </button>
          <button
            onClick={() => navigate(`/sales/${completed.id}`)}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 px-5 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
          >
            <i className="ri-eye-line" /> Ver factura
          </button>
        </div>
      </div>
    );
  }

  if (isCashier && !activeBranch) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        <p className="mt-4 text-foreground-600">Cargando tu sucursal...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-8rem)] lg:min-h-0 lg:h-[calc(100vh-6.25rem)] lg:overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            POS / Nueva venta
          </h1>
          <p className="mt-1 text-foreground-600 flex items-center gap-2">
            <i className="ri-building-2-line" />
            {sellBranch ? `Sucursal: ${sellBranch.name}` : 'Sin sucursal seleccionada'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setWholesaleMode((v) => !v)}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold whitespace-nowrap transition cursor-pointer ${
              wholesaleMode
                ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                : 'border border-background-300 text-foreground-700 hover:bg-background-100'
            }`}
            title="Vender al por mayor"
          >
            <i className="ri-store-3-line" />
            <span>Mayorista</span>
            {wholesaleMode && <i className="ri-checkbox-circle-fill" />}
          </button>
          <button
            onClick={() => navigate('/cash')}
            className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            title="Cuadre de caja"
          >
            <i className="ri-wallet-3-line" />
            <span>Cuadre de caja</span>
          </button>
          <button
            onClick={() => setShowShortcuts(true)}
            className="hidden sm:inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            title="Atajos de teclado"
          >
            <i className="ri-keyboard-line" />
            <span>Atajos</span>
          </button>
          <button
            onClick={toggleSound}
            className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            title={soundOn ? 'Silenciar sonido' : 'Activar sonido'}
          >
            <i className={soundOn ? 'ri-volume-up-line' : 'ri-volume-mute-line'} />
            <span className="hidden sm:inline">{soundOn ? 'Sonido' : 'Silencio'}</span>
          </button>
        </div>
      </div>

      {/* Buscadores unificados (Cliente / Producto / Descuento) */}
      <div className="mt-4 flex items-start gap-2">
        {/* Cliente / Mayorista */}
        {activeSearch === 'client' ? (
          <div className="flex-1 min-w-0">
            <ClientPicker
              clients={clients}
              customer={customer}
              onChange={setCustomer}
              onAdd={() => setClientModal(true)}
              autoFocus
              onDismiss={() => setActiveSearch(null)}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setActiveSearch('client')}
            className={`shrink-0 w-12 h-12 rounded-xl border flex items-center justify-center transition cursor-pointer ${
              customer
                ? 'border-accent-300 bg-accent-50 text-accent-700'
                : 'border-background-200/70 bg-background-100/70 text-foreground-700 hover:bg-background-200/70'
            }`}
            title="Buscar cliente / mayorista"
          >
            <i className="ri-user-3-line text-xl" />
          </button>
        )}

        {/* Producto */}
        {activeSearch === 'product' ? (
          <div className="flex-1 min-w-0 flex items-start gap-2">
            <div className="relative flex-1 min-w-0">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-base pointer-events-none" />
              <input
                ref={searchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Buscar por nombre, SKU o código de barra... (F1)"
                className="w-full rounded-xl border border-background-200/70 bg-background-100/70 backdrop-blur-md pl-11 pr-4 py-3 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 placeholder:text-foreground-400"
              />
            </div>
            <button
              onClick={() => setShowScanner(true)}
              className="shrink-0 w-12 h-12 rounded-xl border border-background-200/70 bg-background-100/70 backdrop-blur-md flex items-center justify-center text-foreground-700 hover:bg-background-200/70 cursor-pointer"
              title="Escanear con cámara"
            >
              <i className="ri-qr-scan-2-line text-xl" />
            </button>
            <button
              type="button"
              onClick={() => setActiveSearch(null)}
              className="shrink-0 w-12 h-12 rounded-xl border border-background-200/70 bg-background-100/70 backdrop-blur-md flex items-center justify-center text-foreground-500 hover:bg-background-200/70 cursor-pointer"
              title="Cerrar búsqueda"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setActiveSearch('product')}
            className="shrink-0 w-12 h-12 rounded-xl border border-background-200/70 bg-background-100/70 text-foreground-700 hover:bg-background-200/70 flex items-center justify-center transition cursor-pointer"
            title="Buscar producto"
          >
            <i className="ri-shopping-bag-3-line text-xl" />
          </button>
        )}

        {/* Código de descuento */}
        {activeSearch === 'discount' ? (
          <div className="flex-1 min-w-0 flex items-start gap-2">
            <div className="relative flex-1 min-w-0">
              <i className="ri-coupon-3-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none" />
              <input
                ref={discountRef}
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyCode(discountCode);
                  }
                }}
                placeholder="Código de descuento (CAJ- / ADMIN-)"
                className="w-full rounded-xl border border-background-200/70 bg-background-100/70 backdrop-blur-md pl-10 pr-3 py-3 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 placeholder:text-foreground-400"
              />
            </div>
            <button
              onClick={() => applyCode(discountCode)}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-secondary-500 hover:bg-secondary-600 px-4 py-3 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
            >
              <i className="ri-check-line" /> Aplicar
            </button>
            <button
              type="button"
              onClick={() => setActiveSearch(null)}
              className="shrink-0 w-12 h-12 rounded-xl border border-background-200/70 bg-background-100/70 flex items-center justify-center text-foreground-500 hover:bg-background-200/70 cursor-pointer"
              title="Cerrar búsqueda"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setActiveSearch('discount')}
            className={`shrink-0 w-12 h-12 rounded-xl border flex items-center justify-center transition cursor-pointer ${
              appliedCode
                ? 'border-accent-300 bg-accent-50 text-accent-700'
                : 'border-background-200/70 bg-background-100/70 text-foreground-700 hover:bg-background-200/70'
            }`}
            title="Código de descuento"
          >
            <i className="ri-coupon-3-line text-xl" />
          </button>
        )}
      </div>

      {scanMsg && (
        <div className="mt-3 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-sm text-foreground-700">
          {scanMsg}
        </div>
      )}

      {wholesaleMode && (
        <div className="mt-3 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-sm text-foreground-700 flex items-center gap-2">
          <i className="ri-store-3-line text-accent-700" />
          <span>Modo mayorista activado — los productos muestran su precio mayorista.</span>
        </div>
      )}

      {codeMsg && <p className="mt-2 text-xs text-accent-700">{codeMsg}</p>}
      {appliedCode && (
        <div className="mt-2 inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-1.5">
          <span className="text-xs font-semibold text-foreground-950">
            {appliedCode.scope === 'admin' ? 'DESCUENTO ADMIN' : 'DESCUENTO CAJERO'} -
            {appliedCode.percentage}% · {appliedCode.code}
          </span>
          <button
            onClick={() => {
              setAppliedCode(null);
              setDiscountCode('');
            }}
            className="w-5 h-5 flex items-center justify-center rounded text-foreground-500 hover:text-foreground-900 cursor-pointer"
            title="Quitar código"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      )}

      {/* Contenido */}
      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1fr_400px] lg:grid-rows-[minmax(0,1fr)] gap-6 flex-1 lg:min-h-0">
        {/* Productos (única zona que hace scroll) */}
        <div className="lg:min-h-0 lg:overflow-y-auto lg:pr-2">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
            </div>
          ) : error ? (
            <div className="rounded-lg border border-accent-200 bg-accent-50 p-8 text-center">
              <i className="ri-error-warning-line text-4xl text-accent-700" />
              <p className="mt-3 text-foreground-800">{friendlyError(error, 'No se pudieron cargar los productos.')}</p>
              <button
                onClick={reload}
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
              >
                <i className="ri-refresh-line" /> Reintentar
              </button>
            </div>
          ) : products.length === 0 ? (
            <div className="rounded-lg border border-dashed border-background-300 p-12 text-center">
              <i className="ri-search-line text-4xl text-foreground-400" />
              <p className="mt-3 text-foreground-600">No se encontraron productos.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
                {products.map((product) => {
                  const inCart = cart.find((i) => i.product.id === product.id);
                  const outOfStock = !allowSellWithoutStock && product.stock <= 0;
                  return (
                    <button
                      key={product.id}
                      onClick={() => addToCart(product)}
                      disabled={outOfStock}
                      className={`group text-left rounded-xl border border-background-200 bg-background-100/50 p-2.5 transition cursor-pointer ${
                        outOfStock
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:border-primary-300 hover:bg-primary-50/40'
                      } ${inCart ? 'ring-2 ring-primary-400' : ''}`}
                    >
                      <div className="relative w-full h-28 sm:h-32 rounded-lg overflow-hidden bg-background-100">
                        <ProductImage
                          url={product.image_url}
                          name={product.name}
                          className="w-full h-full object-cover"
                          placeholderClassName="w-full h-full p-0"
                          iconClassName="text-2xl"
                          showName={false}
                        />
                        {inCart && (
                          <span className="absolute top-1.5 right-1.5 min-w-[22px] h-[22px] px-1.5 rounded-full bg-primary-500 text-white text-[11px] font-bold flex items-center justify-center">
                            {inCart.quantity}
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-sm font-medium text-foreground-950 leading-snug line-clamp-2">
                        {product.name}
                      </p>
                      {wholesaleMode && product.wholesale_price != null && product.wholesale_price > 0 && (
                        <span className="mt-1 inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-accent-500 text-background-50">
                          MAYORISTA
                        </span>
                      )}
                      <div className="mt-1.5 flex items-end justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-primary-700">
                            {wholesaleMode && product.wholesale_price != null && product.wholesale_price > 0
                              ? formatMoney(product.wholesale_price, currencySymbol)
                              : formatMoney(product.sale_price, currencySymbol)}
                          </p>
                          <p className={`text-xs ${product.stock <= 0 ? 'text-accent-700' : 'text-foreground-500'}`}>
                            {product.stock <= 0 ? 'Agotado' : `Stock: ${product.stock}`}
                          </p>
                        </div>
                        <span
                          className={`shrink-0 w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center transition ${
                            outOfStock ? '' : 'group-hover:bg-primary-600'
                          }`}
                        >
                          <i className="ri-add-line text-base" />
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {hasMore && (
                <div className="mt-5 flex justify-center">
                  <button
                    onClick={loadMore}
                    className="inline-flex items-center gap-2 rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
                  >
                    <i className="ri-arrow-down-line" /> Cargar más
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Carrito (desktop, fijo) */}
        <div className="hidden lg:block lg:min-h-0">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4 h-full">
            <CartPanel
              cart={cart}
              pricing={pricing}
              currencySymbol={currencySymbol}
              canApplyDiscount={canApplyDiscount}
              maxPercentage={discountMaxPercentage}
              isAdmin={isAdmin}
              fixedAllowed={discountFixedAllowed}
              taxesEnabled={taxesEnabled}
              subtotal={totals.subtotal}
              discount={totals.discount}
              tax={totals.tax}
              total={totals.total}
              onIncrement={increment}
              onDecrement={decrement}
              onRemove={remove}
              onEditDiscount={setDiscountLineId}
              onApplyPercent={applyQuickPercent}
              onCheckout={() => setShowCheckout(true)}
              selectedLineId={selectedLineId}
              onSelectLine={setSelectedLineId}
            />
          </div>
        </div>
      </div>

      {/* Barra de carrito (móvil) */}
      <div className="lg:hidden fixed bottom-14 left-0 right-0 z-30 px-3 pb-2">
        <button
          onClick={() => setCartOpen(true)}
          className="w-full flex items-center justify-between rounded-xl bg-gradient-to-r from-primary-700 via-primary-600 to-accent-600 px-4 py-3 text-white cursor-pointer"
        >
          <span className="flex items-center gap-2 text-sm font-medium">
            <i className="ri-shopping-basket-line text-base" />
            {cart.length} {cart.length === 1 ? 'producto' : 'productos'}
          </span>
          <span className="font-heading text-lg font-bold">{formatMoney(totals.total, currencySymbol)}</span>
        </button>
      </div>

      {/* Carrito (móvil, pantalla completa) */}
      {cartOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex flex-col bg-background-50">
          <div className="flex items-center justify-between px-4 h-14 border-b border-background-200">
            <h2 className="font-heading font-semibold text-foreground-950">Carrito</h2>
            <button
              onClick={() => setCartOpen(false)}
              className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
          <div className="flex-1 overflow-hidden p-4">
            <CartPanel
              cart={cart}
              pricing={pricing}
              currencySymbol={currencySymbol}
              canApplyDiscount={canApplyDiscount}
              maxPercentage={discountMaxPercentage}
              isAdmin={isAdmin}
              fixedAllowed={discountFixedAllowed}
              taxesEnabled={taxesEnabled}
              subtotal={totals.subtotal}
              discount={totals.discount}
              tax={totals.tax}
              total={totals.total}
              onIncrement={increment}
              onDecrement={decrement}
              onRemove={remove}
              onEditDiscount={setDiscountLineId}
              onApplyPercent={applyQuickPercent}
              onCheckout={() => {
                setCartOpen(false);
                setShowCheckout(true);
              }}
              selectedLineId={selectedLineId}
              onSelectLine={setSelectedLineId}
            />
          </div>
        </div>
      )}

      {/* Cobro */}
      <CheckoutModal
        open={showCheckout}
        onClose={() => setShowCheckout(false)}
        total={totals.total}
        currencySymbol={currencySymbol}
        paymentMethods={paymentMethods}
        requireReceived={requireReceived}
        onConfirm={handleConfirm}
        processing={processing}
      />

      {/* Escáner */}
      <BarcodeScanner
        open={showScanner}
        onClose={() => setShowScanner(false)}
        onDetected={(code) => {
          setShowScanner(false);
          handleScan(code);
        }}
      />

      {/* Agregar cliente */}
      <Modal open={clientModal} onClose={() => setClientModal(false)} title="Agregar cliente">
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">Nombre *</label>
            <input
              value={newClientName}
              onChange={(e) => setNewClientName(e.target.value)}
              autoFocus
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">Teléfono</label>
            <input
              value={newClientPhone}
              onChange={(e) => setNewClientPhone(e.target.value)}
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <button
            onClick={handleAddClient}
            disabled={!newClientName.trim()}
            className="w-full rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
          >
            Guardar cliente
          </button>
        </div>
      </Modal>

      {/* Atajos de teclado */}
      <Modal open={showShortcuts} onClose={() => setShowShortcuts(false)} title="Atajos de teclado">
        <div className="rounded-lg border border-background-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background-100 text-foreground-600 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">Tecla</th>
                <th className="px-3 py-2 font-medium">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {SHORTCUTS.map((s) => (
                <tr key={s.key}>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center justify-center min-w-[3rem] px-2 py-0.5 rounded-md border border-background-300 bg-background-100 font-mono text-xs font-semibold text-foreground-900">
                      {s.key}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-foreground-700">{s.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>

      {/* Cambiar cantidad */}
      <Modal open={qtyModal} onClose={() => setQtyModal(false)} title="Cambiar cantidad">
        <div className="space-y-3">
          <p className="text-foreground-700">
            {selectedLine?.product.name} · Stock disponible: {selectedLine?.product.stock}
          </p>
          <input
            type="number"
            min="1"
            value={qtyValue}
            onChange={(e) => setQtyValue(e.target.value)}
            autoFocus
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
          <button
            onClick={applyQty}
            className="w-full rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
          >
            Guardar
          </button>
        </div>
      </Modal>

      {/* Descuento por artículo */}
      <DiscountModal
        open={discountLineId != null}
        item={discountLine}
        pricing={discountLineId ? pricing[discountLineId] ?? null : null}
        currencySymbol={currencySymbol}
        percentageAllowed={discountPercentageAllowed}
        fixedAllowed={discountFixedAllowed}
        maxPercentage={discountMaxPercentage}
        isAdmin={isAdmin}
        authorizerName={currentUserName}
        onClose={() => setDiscountLineId(null)}
        onApply={(discount) => {
          if (discountLineId) setManualDiscount(discountLineId, discount);
        }}
      />

      {/* Confirmar eliminar */}
      <Modal
        open={removeConfirm}
        onClose={() => setRemoveConfirm(false)}
        title="Eliminar producto"
        footer={
          <>
            <button
              onClick={() => setRemoveConfirm(false)}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={() => {
                if (selectedLine) remove(selectedLine.product.id);
                setRemoveConfirm(false);
              }}
              className="rounded-md bg-accent-500 hover:bg-accent-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
            >
              Eliminar
            </button>
          </>
        }
      >
        <p className="text-foreground-700">
          ¿Eliminar <span className="font-medium text-foreground-950">{selectedLine?.product.name}</span> de la venta?
        </p>
      </Modal>

      {/* Confirmar nueva venta */}
      <Modal
        open={newSaleConfirm}
        onClose={() => setNewSaleConfirm(false)}
        title="Nueva venta"
        footer={
          <>
            <button
              onClick={() => setNewSaleConfirm(false)}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={() => {
                resetSale();
                setNewSaleConfirm(false);
              }}
              className="rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
            >
              Descartar y empezar
            </button>
          </>
        }
      >
        <p className="text-foreground-700">
          Hay una venta sin finalizar. Si continúas, se descartarán los productos del carrito.
        </p>
      </Modal>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-20 lg:bottom-6 left-1/2 -translate-x-1/2 z-[60] rounded-md bg-foreground-950 text-background-50 px-4 py-2.5 text-sm font-medium shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}